import { AppSettings } from "@/AppSettings";
import { ConfigurationStatus, CoordinatingGroup, Institution, MasterGroup, NoteStatus, Participant, SourceDataPriority, SourceDataStatus, Trial } from "@/api/dtos";
import CoordinatingGroupsContext from "@/contexts/data/CoordinatingGroupsContext";
import InstitutionsContext from "@/contexts/data/InstitutionsContext";
import MasterGroupsContext from "@/contexts/data/MasterGroupsContext";
import ParticipantsContext from "@/contexts/data/ParticipantsContext";
import TrialContext from "@/contexts/data/TrialContext";
import useParticipantStatuses from "@/hooks/data/useParticipantStatuses";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Divider, Fade, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Paper, Switch, Theme, Tooltip, TooltipProps, Typography, darken, lighten, listItemButtonClasses, listItemClasses, listItemSecondaryActionClasses, styled, tooltipClasses, useTheme } from "@mui/material";
import { grey } from "@mui/material/colors";
import { DateTime } from "luxon";
import { ComponentType, FC, ReactNode, memo, useCallback, useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Virtuoso } from "react-virtuoso";
import DefaultStatComponents, { StatusListStatsBox } from "./StatusListStats";
import StatusListSort, { IStatusListSortable } from "./StatusListSort";
import { useSessionStorage } from "react-use";
import SearchDialog, { ISearchField, ISearchForm } from "@/components/dialogs/SearchDialog";
import { InputComponentType } from "@ngt/forms-core";
import React from "react";
import noteStatusToText from "@/utilities/configuration/noteStatusToText";


export interface IStatusListDataObject {
    id?: number;
    status?: ConfigurationStatus;
} 

export interface IStatusListItemData<TData extends IStatusListDataObject = any, TAdditional extends object = any> {
    data: TData;
    status?: SourceDataStatus;
    additional: TAdditional;
    calculated: {
        queriesOpen: number;
        queriesTotal: number;
        queriesComplete: number;
        formsComplete: number;
        reviewComplete: number;
        verificationComplete: number;
        verificationPriority?: SourceDataPriority;
        reviewPriority?: SourceDataPriority;
    },
    render: {
        listItemDisabled: boolean;
        disabled: boolean;
        link: string;
        label: ReactNode;
        configureLink?: string;
        StatsComponent?: ComponentType<IStatusListStatsComponentProps<TData, TAdditional>>;
        queueUpdate?: () => Promise<any>
    };
}

export interface IStatusListStatsComponentProps<TData extends IStatusListDataObject = any, TAdditional extends object = any> {
    itemData: Omit<IStatusListItemData<TData, TAdditional>, 'render' > & { render: Omit<IStatusListItemData<TData, TAdditional>['render'], 'stats'>};
    theme: Theme;
}

export interface IStatusListFiltersComponentProps {
}

export interface IStatusListCreateAdditional<TData extends IStatusListDataObject = any, TAdditional extends object = any> {
    (itemData: Omit<IStatusListItemData<TData, TAdditional>, 'render' | 'additional' | 'calculated'>): TAdditional;
}

export interface IStatusListCreateCalculatedFields<TData extends IStatusListDataObject = any, TAdditional extends object = any> {
    (itemData: Omit<IStatusListItemData<TData, TAdditional>, 'render' | 'calculated' >): IStatusListItemData<TData, TAdditional>["calculated"];
}

export interface IStatusListCreateLabel<TData extends IStatusListDataObject = any, TAdditional extends object = any> {
    (itemData: Omit<IStatusListItemData<TData, TAdditional>, 'render'>, theme: Theme): ReactNode;
}

export interface IStatusListCreateLink<TData extends IStatusListDataObject = any, TAdditional extends object = any> {
    (itemData: Omit<IStatusListItemData<TData, TAdditional>, 'render'>): string;
}

export interface IStatusListCreateConfigureLink<TData extends IStatusListDataObject = any, TAdditional extends object = any> {
    (itemData: Omit<IStatusListItemData<TData, TAdditional>, 'render'>): string | undefined;
}

export interface IStatusListIsDisabled<TData extends IStatusListDataObject = any, TAdditional extends object = any> {
    (itemData: Omit<IStatusListItemData<TData, TAdditional>, 'render'>): boolean;
}

export interface IStatusListSearch<TData extends IStatusListDataObject = any, TAdditional extends object = any, TValue = any, TComponent extends InputComponentType = any> extends ISearchField<TComponent> {
    searchFn: (item: IStatusListItemData<TData, TAdditional>, value?: TValue) => boolean;
}

export interface IStatusListProps<TData extends IStatusListDataObject = any, TAdditional extends object = any> {
    data?: TData[];
    statuses?: Record<number, SourceDataStatus>;
    createAdditional: IStatusListCreateAdditional<TData, TAdditional>;
    createLabel: IStatusListCreateLabel<TData, TAdditional>;
    isDisabled: IStatusListIsDisabled<TData, TAdditional>;
    isListItemDisabled?: IStatusListIsDisabled<TData, TAdditional>;
    createConfigureLink: IStatusListCreateConfigureLink<TData, TAdditional>;
    createLink: IStatusListCreateLink<TData, TAdditional>;
    createCalculatedFields?: IStatusListCreateCalculatedFields<TData, TAdditional>;
    StatsComponent?: ComponentType<IStatusListStatsComponentProps<TData, TAdditional>>;
    FiltersComponent?: ComponentType<IStatusListFiltersComponentProps>;
    queueUpdate?: (data: TData) => Promise<any>;
    sortKey: string;
    sortableFields?: IStatusListSortable<TData, TAdditional>[],
    noRecords?: ReactNode;
    searchKey: string;
    searchFields?: IStatusListSearch<TData, TAdditional>[];
    fixedItemHeight?: number;
    defaultSearchData?: Record<string, any>;
    disableVirtual?: boolean;
}

export const StatusListItem = styled(ListItem)(({ theme }) => ({
    [`& > .${listItemButtonClasses.root}`]: {
        paddingRight: theme.spacing(0)
    },
    [`& > .${listItemSecondaryActionClasses.root}`]: {
        right: theme.spacing(0)
    }
}));

export const StatusListItemButton = styled(ListItemButton)(({ theme }) => ({
    padding: theme.spacing(0, 2),
    minHeight: theme.spacing(6)
}));

export const StatusListItemText = styled(ListItemText)(({ theme }) => ({
    margin: theme.spacing(0.5, 0)
}));

export const StatusListItemIcon = styled(ListItemIcon)(({ theme }) => ({
    minWidth: theme.spacing(6)
}));


export const RoundBox = styled(Box)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden'
}));

const FilterSortBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(-7),
    height: theme.spacing(7),
    alignItems: 'top'
}));

const FilterSortBoxInner = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'stretch',
    maxHeight: theme.spacing(4.75)
}));

const SortBox = styled(Paper)(({ theme }) => ({
    marginLeft: theme.spacing(3),
    //marginBottom: theme.spacing(3),
    display: 'flex'
}));

const DisabledBox = styled(Paper)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginLeft: theme.spacing(3),
    paddingLeft: theme.spacing(2),
    //marginBottom: theme.spacing(3)
}));

const LegendBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    alignItems: 'center',
    //marginBottom: theme.spacing(3),
    minHeight: theme.spacing(4.75)
}));

const SearchBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    alignItems: 'center',
    //marginBottom: theme.spacing(3),
    minHeight: theme.spacing(4.75),
    marginLeft: theme.spacing(3),
}));

const SearchButton = styled(Button)(({ theme }) => ({
    minWidth: 0,
    padding: theme.spacing(0),
    width: theme.spacing(4.75),
    alignSelf: 'stretch'
}));

const LegendTooltipBox = styled(Box) (({theme}) => ({
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1)
}));

const LegendAnchor = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    minWidth: 0,
    padding: theme.spacing(0),
    width: theme.spacing(4.75),
    alignSelf: 'stretch',
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2]
}));

const LegendEntry = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
}));

const LegendLabel = styled(Typography)(({ theme }) => ({
    marginLeft: theme.spacing(1)
}));

const LegendIcon = styled(FontAwesomeIcon) (() => ({

}));



interface IVirtuosoContext<TData extends IStatusListDataObject = any, TAdditional extends object = any> {
    theme: Theme;
}

interface IVirtuosoInnerItem<TData extends IStatusListDataObject = any, TAdditional extends object = any> {
    index: number;
    item: IStatusListItemData<TData, TAdditional>;
    context: IVirtuosoContext<TData, TAdditional>;
}

// Item contents are cached properly with React.memo
const InnerItem = <TData extends IStatusListDataObject = any, TAdditional extends object = any>({ 
    index,
    item,
    context
}: IVirtuosoInnerItem<TData, TAdditional>) => {
    const StatsComponent = item.render.StatsComponent;

    return (
        <>
            <StatusListItem 
                disablePadding
                secondaryAction={
                    StatsComponent && <StatsComponent itemData={item} theme={context.theme} />
                }
            >
                <StatusListItemButton
                    //@ts-ignore-next-line
                    component={Link} 
                    to={item.render.link} 
                    disabled={item.render.listItemDisabled}
                >
                    {item.render.label}
                </StatusListItemButton>
            </StatusListItem>
            <Divider />
        </>
    )
};

const MemoizedInnerItem = memo(InnerItem) as typeof InnerItem;

// The callback is executed often - don't inline complex components in here.
const virtuosoItemContent = <TData extends IStatusListDataObject = any, TAdditional extends object = any>(index: number, item: IStatusListItemData<TData, TAdditional>, context: IVirtuosoContext<TData, TAdditional>) => {
    return <MemoizedInnerItem index={index} item={item} context={context} />
}


export const DEFAULT_createCalculatedFields: IStatusListCreateCalculatedFields = (itemData) => {
    const openQueries = 
        (itemData.status?.siteQueryNewCount ?? 0) + 
        (itemData.status?.siteQueryUpdatedCount ?? 0) + 
        (itemData.status?.siteQueryResolutionProposedCount ?? 0) + 
        (itemData.status?.internalQueryNewCount ?? 0) + 
        (itemData.status?.internalQueryUpdatedCount ?? 0) + 
        (itemData.status?.internalQueryResolutionProposedCount ?? 0) + 
        (itemData.status?.systemQueryNewCount ?? 0) + 
        (itemData.status?.systemQueryUpdatedCount ?? 0) + 
        (itemData.status?.systemQueryResolutionProposedCount ?? 0);

    const totalQueries = 
        (itemData.status?.siteQueryTotalCount ?? 0) + 
        (itemData.status?.internalQueryTotalCount ?? 0) + 
        (itemData.status?.systemQueryTotalCount ?? 0);

    let verificationPriority = undefined;

    if (((itemData.status?.verificationCriticalPriorityCompleteCount ?? 0) + (itemData.status?.verificationCriticalPriorityNotApplicableCount ?? 0)) !== (itemData.status?.verificationCriticalPriorityTotalCount ?? 0)) {
        verificationPriority = SourceDataPriority.Critical;
    }
    else if (((itemData.status?.verificationHighPriorityCompleteCount ?? 0) + (itemData.status?.verificationHighPriorityNotApplicableCount ?? 0)) !== (itemData.status?.verificationHighPriorityTotalCount ?? 0)) {
        verificationPriority = SourceDataPriority.High;
    }
    else if (((itemData.status?.verificationNormalPriorityCompleteCount ?? 0) + (itemData.status?.verificationNormalPriorityNotApplicableCount ?? 0)) !== (itemData.status?.verificationNormalPriorityTotalCount ?? 0)) {
        verificationPriority = SourceDataPriority.Normal;
    }
    else if (((itemData.status?.verificationLowPriorityCompleteCount ?? 0) + (itemData.status?.verificationLowPriorityNotApplicableCount ?? 0)) !== (itemData.status?.verificationLowPriorityTotalCount ?? 0)) {
        verificationPriority = SourceDataPriority.Low;
    }

    let reviewPriority = undefined;

    if (((itemData.status?.reviewCriticalPriorityCompleteCount ?? 0) + (itemData.status?.reviewCriticalPriorityNotApplicableCount ?? 0)) !== (itemData.status?.reviewCriticalPriorityTotalCount ?? 0)) {
        reviewPriority = SourceDataPriority.Critical;
    }
    else if (((itemData.status?.reviewHighPriorityCompleteCount ?? 0) + (itemData.status?.reviewHighPriorityNotApplicableCount ?? 0)) !== (itemData.status?.reviewHighPriorityTotalCount ?? 0)) {
        reviewPriority = SourceDataPriority.High;
    }
    else if (((itemData.status?.reviewNormalPriorityCompleteCount ?? 0) + (itemData.status?.reviewNormalPriorityNotApplicableCount ?? 0)) !== (itemData.status?.reviewNormalPriorityTotalCount ?? 0)) {
        reviewPriority = SourceDataPriority.Normal;
    }
    else if (((itemData.status?.reviewLowPriorityCompleteCount ?? 0) + (itemData.status?.reviewLowPriorityNotApplicableCount ?? 0)) !== (itemData.status?.reviewLowPriorityTotalCount ?? 0)) {
        reviewPriority = SourceDataPriority.Low;
    }

    return {
        reviewPriority,
        verificationPriority,
        queriesOpen: openQueries,
        queriesTotal: totalQueries,
        queriesComplete: totalQueries ? (1 - (openQueries) / (totalQueries)) * 100 : 100,
        formsComplete: itemData.status?.formsTotalCount ? (itemData.status?.formsCompleteCount ?? 0) / (itemData.status?.formsTotalCount ?? 1) * 100 : 100,
        reviewComplete: itemData.status?.reviewTotalCount ? ((itemData.status?.reviewCompleteCount ?? 0) + (itemData.status?.reviewNotApplicableCount ?? 0)) / (itemData.status?.reviewTotalCount ?? 1) * 100 : 100,
        verificationComplete: itemData.status?.verificationTotalCount ? ((itemData.status?.verificationCompleteCount ?? 0) + (itemData.status?.verificationNotApplicableCount ?? 0)) / (itemData.status?.verificationTotalCount ?? 1) * 100 : 100,
    }
}


const DefaultStatsComponent = <TData extends IStatusListDataObject = any, TAdditional extends object = any>({
    itemData, 
    theme
}: IStatusListStatsComponentProps<TData,TAdditional>) => {
    return (
        <StatusListStatsBox>
            <DefaultStatComponents.DisabledIndicatorStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.ReadOnlyIndicatorStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.ParticipantStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.FormStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.NoteStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.ReviewStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.VerificationStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.UpdateButton itemData={itemData} theme={theme} />
        </StatusListStatsBox>
    )
};

const StatusList = <TData extends IStatusListDataObject = any, TAdditional extends object = any>({
    data,
    statuses,
    createLabel,
    createAdditional,
    createConfigureLink,
    createLink,
    isDisabled,
    isListItemDisabled,
    createCalculatedFields,
    StatsComponent,
    FiltersComponent,
    //createStats,
    queueUpdate,
    sortKey,
    sortableFields,
    noRecords,
    searchFields,
    searchKey,
    fixedItemHeight,
    defaultSearchData,
    disableVirtual
}: IStatusListProps<TData, TAdditional>) => {
    const theme = useTheme();

    const calculatedListItems: IStatusListItemData<TData, TAdditional>[] = useMemo(() => {
        return data?.filter(x => x.id).map((d, i) => {

            const status = statuses?.[d.id!]

            const itemData = {
                data: d,
                status: status
            } as IStatusListItemData<TData, TAdditional>;


            itemData.additional = createAdditional(itemData);

            itemData.calculated = createCalculatedFields?.(itemData) ?? DEFAULT_createCalculatedFields(itemData);

            itemData.render = {
                link: createLink(itemData),
                label: createLabel(itemData, theme),
                disabled: isDisabled(itemData),
                listItemDisabled: isListItemDisabled?.(itemData) ?? isDisabled(itemData),
                configureLink: createConfigureLink(itemData),
                queueUpdate: !!queueUpdate ? async () => { await queueUpdate(d); } :  undefined,
                StatsComponent: StatsComponent ?? DefaultStatsComponent
            }

            //itemData.render.stats = createStats?.(itemData, theme) ?? DEFAULT_createStats(itemData, theme);

            return itemData;
        }) ?? [];
    }, [data, statuses, createLink, createLabel, isDisabled, isListItemDisabled, createConfigureLink, createCalculatedFields, /* createStats,*/ StatsComponent, queueUpdate, theme])

    const virtuosoContext = useMemo(() => {
        return {
            theme
        } as IVirtuosoContext<TData, TAdditional>;
    }, [theme]);
    
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchData, setSearchData] = useSessionStorage<Record<string, any>>(searchKey, defaultSearchData);
    const [selectedSort, setSelectedSort] = useState<undefined | {field: IStatusListSortable<TData, TAdditional>; direction: 'asc' | 'desc'}>(undefined);

    const onSortChange = useCallback((sort : {field: IStatusListSortable<TData, TAdditional>; direction: 'asc' | 'desc'}) => {
        setSelectedSort(sort)
    }, [setSelectedSort])
    
    const onSearchClose = useCallback(() => {
        setSearchOpen(false);
    }, [setSearchOpen])

    const onSearchOk = useCallback(async (data?: ISearchForm) => {
        setSearchData(data as Record<string, any>);
    }, [setSearchData])

    const sortedListItems = useMemo(() => {
        
        let newData = [...calculatedListItems];

        if (!!selectedSort?.field  && !!selectedSort.direction && newData) {
            newData = newData.sort((a,b) => selectedSort.field.sort(a,b) * (selectedSort.direction === 'desc' ? -1 : 1));
        }

        if (searchData && searchFields) {
            const searchedFields = searchFields.filter(x => !!searchData[x.key]);

            newData = newData?.filter(x => !searchedFields.some(y => !y.searchFn(x, searchData[y.key])))
        }

        return newData;
    }, [calculatedListItems, selectedSort, sortableFields, searchData, searchFields])

    const filterCount = useMemo(() => {
        const searchedFields = searchFields?.filter(x => !!searchData?.[x.key]) ?? [];

        return searchedFields.length;
    }, [searchData, searchFields]);


    const hasSearch = searchFields && searchFields.length > 0 ;
    const hasSort = sortableFields && sortableFields.length > 0;

    const noteLegend = useMemo(() => {
        const legendEntries = [];
        for(let statusMapping in AppSettings.noteStatusColors) {
            const typedStatusMapping = statusMapping as unknown as NoteStatus;
            const statusColor = AppSettings.noteStatusColors[typedStatusMapping];
            // const color = AppSettings.noteStatusColors[statusMapping];
            // if(AppSettings.noteStatusColors[statusMapping])
            if(statusColor) {
                legendEntries.push(
                    <LegendEntry key={`note-status-legend-${statusMapping}`}>
                        <LegendIcon 
                            sx={{
                                color: theme.palette.mode === 'dark' ? `${statusColor}.light` : `${statusColor}.dark`
                            }}
                            icon={AppSettings.icons.note} size="2x" /> 
                        <LegendLabel variantMapping={{"body1": "span"}}>{noteStatusToText(typedStatusMapping)}</LegendLabel>
                    </LegendEntry>
                );
            }
        }

        return legendEntries;
    }, [AppSettings.noteStatusColors, theme]);

    const hasNoteLegend = useMemo(() => {
        return noteLegend.length > 0;
    }, [noteLegend]);


    return (
        <>
            {
                (hasNoteLegend || hasSort || hasSearch || FiltersComponent) && (
                    <FilterSortBox>
                        <FilterSortBoxInner>
                            {
                                hasNoteLegend && (
                                    <LegendBox>
                                        <Tooltip
                                            slotProps={{
                                                tooltip: {
                                                    sx: {
                                                        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
                                                        color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
                                                    }
                                                },
                                                popper: {
                                                    sx: {
                                                        opacity: 0.9
                                                    }
                                                }
                                            }}
                                            title={<LegendTooltipBox>{...noteLegend}</LegendTooltipBox>}
                                        >
                                            <LegendAnchor>
                                                <FontAwesomeIcon color="white" icon={AppSettings.icons.note} size="lg" />
                                            </LegendAnchor>
                                        </Tooltip>
                                    </LegendBox>
                                )
                            }
                            {
                                hasSearch && (
                                    <SearchBox>
                                        <Tooltip title="Filter">
                                            <SearchButton onClick={() => setSearchOpen(true)} color={!!filterCount ? "primary" : "inherit"} variant="contained">
                                                <FontAwesomeIcon icon={AppSettings.icons.filter} size="lg" />
                                            </SearchButton>
                                        </Tooltip>
                                    </SearchBox>
                                )
                            }
                            {
                                hasSort && (
                                    <SortBox>
                                        <StatusListSort 
                                            sortableFields={sortableFields}
                                            sortKey={sortKey}
                                            onChange={onSortChange}
                                        />
                                    </SortBox>
                                )
                            }
                            {
                                !!FiltersComponent && (
                                    <FiltersComponent />
                                )
                            }
                        </FilterSortBoxInner>
                    </FilterSortBox>
                )
            }
            {
                (!sortedListItems || sortedListItems.length === 0) && (
                    noRecords
                )
            }
            <Paper>
                <RoundBox>
                    <List disablePadding>
                        {
                            !disableVirtual && sortedListItems.length > 20 && (
                                <Virtuoso 
                                    key={fixedItemHeight}
                                    useWindowScroll
                                    data={sortedListItems}
                                    context={virtuosoContext}
                                    fixedItemHeight={fixedItemHeight}
                                    itemContent={virtuosoItemContent}
                                />  
                            )
                        }
                        {
                            (disableVirtual || sortedListItems.length <= 20) && (
                                sortedListItems.map((x, i) => (
                                    <MemoizedInnerItem key={i} index={i} item={x} context={virtuosoContext} />
                                ))
                            )
                        }              
                    </List>
                </RoundBox>
            </Paper>
            <SearchDialog 
                open={searchOpen}
                searchFields={searchFields}
                formData={searchData}
                onSave={onSearchOk}
                onClose={onSearchClose}
            />
        </>
    )
};

export type StatusListProps<TData extends IStatusListDataObject = any, TAdditional extends object = any> = Omit<IStatusListProps<TData, TAdditional>, 
    'createLabel' | 
    'createAdditional' | 
    'createConfigureLink' | 
    'createLink' | 
    'isDisabled' |
    'createCalculatedFields' |
    'createStats' |
    'sortKey' |
    'sortableFields' |
    'noRecords' |
    'searchFields' |
    'searchKey' |
    'fixedItemHeight' |
    'defaultSearchData'
>;

export default StatusList;